import React, { useState } from "react";
import { View, TextInput, Pressable, Image, ScrollView, Text, Platform, Alert } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";

interface Attachment {
  uri: string;
  name: string;
  type: string;
  size?: number;
}

interface ChatInputProps {
  onSendMessage: (message: string, attachments?: Attachment[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({ onSendMessage, disabled = false, placeholder = "Type a message..." }: ChatInputProps) {
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const handleSend = () => {
    if ((message.trim() || attachments.length > 0) && !disabled) {
      onSendMessage(message.trim(), attachments.length > 0 ? attachments : undefined);
      setMessage("");
      setAttachments([]);
    }
  };

  const handleImagePicker = async () => {
    try {
      // Request permissions first
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (status !== "granted") {
        Alert.alert(
          "Permission Required",
          "To attach photos, we need access to your media library.",
          [{ text: "OK" }]
        );
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsEditing: false,
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        const fileNameParts = asset.uri.split('/');
        const fileName = fileNameParts[fileNameParts.length - 1];

        const attachment = {
          uri: asset.uri,
          name: fileName,
          type: asset.type === 'image' ? 'image/jpeg' : 'video/mp4',
          size: asset.fileSize,
        };

        setAttachments([...attachments, attachment]);
      }
    } catch (error) {
      console.error("Error picking image:", error);
      Alert.alert(
        "Error",
        "There was a problem selecting your image. Please try again."
      );
    }
  };

  const handleDocumentPicker = async () => {
    try {

      const result = await DocumentPicker.getDocumentAsync({
        type: "*/*",
        copyToCacheDirectory: true,
      });

      if (result.canceled === false && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        const attachment = {
          uri: asset.uri,
          name: asset.name,
          type: asset.mimeType || 'application/octet-stream',
          size: asset.size,
        };

        setAttachments([...attachments, attachment]);
      }
    } catch (error) {
      console.error("Error picking document:", error);
      Alert.alert(
        "Error",
        "There was a problem selecting your document. Please try again."
      );
    }
  };

  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const renderAttachmentPreview = () => {
    if (attachments.length === 0) return null;

    return (
      <ScrollView
        horizontal
        className="py-2 px-2 max-h-24"
        showsHorizontalScrollIndicator={false}
      >
        {attachments.map((attachment, index) => (
          <View key={index} className="mr-2 relative">
            {attachment.type.startsWith('image') ? (
              <Image
                source={{ uri: attachment.uri }}
                className="h-20 w-20 rounded-md"
                resizeMode="cover"
              />
            ) : (
              <View className="h-20 w-20 rounded-md bg-gray-200 items-center justify-center">
                <Ionicons name="document" size={24} color="#666" />
                <Text className="text-xs text-gray-600 mt-1" numberOfLines={1}>
                  {attachment.name.length > 8
                    ? attachment.name.substring(0, 8) + '...'
                    : attachment.name}
                </Text>
              </View>
            )}
            <Pressable
              onPress={() => removeAttachment(index)}
              className="absolute top-0 right-0 bg-black bg-opacity-50 rounded-full p-1"
            >
              <Ionicons name="close" size={12} color="white" />
            </Pressable>
          </View>
        ))}
      </ScrollView>
    );
  };

  return (
    <View className="border-t border-gray-200 bg-white border-b">
      {renderAttachmentPreview()}
      <View className="flex-row items-center p-2">
        <View className="flex-row gap-1 mr-1">
          <Pressable
            onPress={handleImagePicker}
            disabled={disabled}
            className={`h-10 w-10 rounded-full items-center justify-center ${disabled ? "bg-gray-300" : "bg-gray-100"
              }`}
          >
            <Ionicons name="image-outline" size={22} color={disabled ? "#999" : "#666"} />
          </Pressable>
          <Pressable
            onPress={handleDocumentPicker}
            disabled={disabled}
            className={`h-10 w-10 rounded-full items-center justify-center ${disabled ? "bg-gray-300" : "bg-gray-100"
              }`}
          >
            <Ionicons name="document-attach-outline" size={22} color={disabled ? "#999" : "#666"} />
          </Pressable>
        </View>
        <TextInput
          className="flex-1 bg-gray-100 text-gray-900 rounded-md px-4 py-2 mr-1 h-10 border border-gray-200"
          placeholder={placeholder}
          value={message}
          onChangeText={setMessage}
          multiline
          maxLength={1000}
          editable={!disabled}
          returnKeyType="default"
        />
        <Pressable
          onPress={handleSend}
          disabled={((!message.trim() && attachments.length === 0) || disabled)}
          className={`h-10 w-10 rounded-full items-center justify-center ${!message.trim() && attachments.length === 0 || disabled ? "bg-gray-300" : "bg-primary"
            }`}
        >
          <Ionicons name="send" size={18} color="white" />
        </Pressable>
      </View>
    </View>
  );
} 